<script lang="ts">
	import { getContext } from 'svelte';
	import { Text } from '@Shelly/shelly-design-system';
	import type { Writable } from 'svelte/store';
	import LottiePlayer from 'src/components/LottiePlayer/LottiePlayer.svelte';
	import { format } from 'svelte-i18n';

	const radarSetupContext: Writable<any> = getContext('radarSetupContext');

	$: sensorConfigured = $radarSetupContext?.sensorConfigured ?? false;
	$: roomBoundaries = $radarSetupContext?.roomBoundaries ?? [];
	$: zonesData = $radarSetupContext?.zonesData ?? [];

	$: roomDrawn = roomBoundaries.length > 0;
	$: zonesAdded = zonesData.filter((z: any) => !z.isMainZone).length;

	interface StatusItem {
		label: string;
		done: boolean;
		hint?: string;
	}

	$: statusItems = [
		{
			label: $format('presence_wizard_finish_status_sensor', { default: 'Sensor configured' }),
			done: sensorConfigured,
			hint: sensorConfigured
				? undefined
				: $format('presence_wizard_finish_status_sensor_hint', {
						default: 'You can update this in Sensor settings at any time.',
					}),
		},
		{
			label: $format('presence_wizard_finish_status_room', { default: 'Room boundaries drawn' }),
			done: roomDrawn,
			hint: roomDrawn
				? undefined
				: $format('presence_wizard_finish_status_room_hint', {
						default: 'Open the zone editor and draw your room outline.',
					}),
		},
		{
			label: $format('presence_wizard_finish_status_zones', {
				values: { count: zonesAdded },
				default: `${zonesAdded} zone${zonesAdded === 1 ? '' : 's'} added`,
			}),
			done: zonesAdded > 0,
			hint:
				zonesAdded > 0
					? undefined
					: $format('presence_wizard_finish_status_zones_hint', {
							default: 'Add detection zones to trigger automations for specific areas.',
						}),
		},
	] satisfies StatusItem[];

	$: allDone = statusItems.every((s) => s.done);
</script>

<div class="finish-step">
	<div class="finish-header">
		<div class="finish-lottie">
			<LottiePlayer
				src="/images/lottie/radar/MakeRoomFinal.json"
				autoplay={true}
				loop={false}
				height={140}
				width={140}
				background="transparent"
			/>
		</div>

		<Text element="h2" class="mb-xs">
			{#if allDone}
				{$format('presence_wizard_finish_title_complete', { default: 'All set!' })}
			{:else}
				{$format('presence_wizard_finish_title', { default: "You're ready to go" })}
			{/if}
		</Text>
		<p class="finish-subtitle">
			{#if allDone}
				{$format('presence_wizard_finish_subtitle_complete', {
					default:
						'Your radar sensor is fully configured. Open the zone editor any time to make adjustments.',
				})}
			{:else}
				{$format('presence_wizard_finish_subtitle', {
					default:
						"Your sensor is connected and tracking. A few optional steps remain — complete them in the zone editor whenever you're ready.",
				})}
			{/if}
		</p>
	</div>

	<div class="status-list">
		{#each statusItems as item}
			<div class="status-item" class:status-item--done={item.done}>
				<div class="status-icon">
					{#if item.done}
						<i class="fas fa-circle-check" aria-hidden="true"></i>
					{:else}
						<i class="fas fa-circle-dashed" aria-hidden="true"></i>
					{/if}
				</div>
				<div class="status-text">
					<p class="status-label">{item.label}</p>
					{#if item.hint}
						<p class="status-hint">{item.hint}</p>
					{/if}
				</div>
			</div>
		{/each}
	</div>

	{#if !allDone}
		<div class="finish-tip">
			<i class="fas fa-hand-point-right" aria-hidden="true"></i>
			{$format('presence_wizard_finish_tip', {
				default:
					'Use "Open zone editor" below to complete the remaining steps on the radar grid.',
			})}
		</div>
	{/if}
</div>

<style>
	.finish-step {
		padding: 4px 0 8px;
	}

	.finish-header {
		text-align: center;
		margin-bottom: 24px;
	}

	.finish-lottie {
		display: flex;
		justify-content: center;
		margin-bottom: 8px;
	}

	.finish-subtitle {
		color: var(--color-text-secondary, rgba(255, 255, 255, 0.55));
		line-height: 1.5;
		max-width: 380px;
		margin: 0 auto;
	}

	.status-list {
		display: flex;
		flex-direction: column;
		gap: 2px;
		margin-bottom: 16px;
	}

	.status-item {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 10px 14px;
		border-radius: 8px;
		background: var(--color-surface-2, rgba(255, 255, 255, 0.04));
	}

	.status-icon {
		flex-shrink: 0;
		font-size: 18px;
		margin-top: 1px;
		color: var(--color-text-muted, rgba(255, 255, 255, 0.3));
	}

	.status-item--done .status-icon {
		color: var(--color-success, #00d5b3);
	}

	.status-label {
		margin: 0 0 2px;
		font-size: 14px;
		font-weight: 500;
	}

	.status-hint {
		margin: 0;
		font-size: 12px;
		color: var(--color-text-muted, rgba(255, 255, 255, 0.4));
		line-height: 1.4;
	}

	.finish-tip {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		padding: 10px 14px;
		border-radius: 8px;
		background: var(--color-warning-alpha, rgba(255, 200, 0, 0.08));
		color: var(--color-warning, #ffc800);
		font-size: 13px;
		line-height: 1.4;
	}

	.finish-tip i {
		flex-shrink: 0;
		margin-top: 1px;
	}
</style>
